import { APIClient } from "../../helpers/api_helper";
import secureApi from "../../utils/secureApi";

const api = new APIClient();

// ─── Bulk Upload Template URLs (CDN paths) ────────────────────────────────────
export const ENROLMENT_TEMPLATE_URLS: Record<"dynamic" | "fixed" | "incomplete", string> = {
  dynamic: "/web/templates/enrollment/Dynamic_Template.xlsx",
  fixed: "/web/templates/enrollment/Fixed_Template.xlsx",
  incomplete: "/web/templates/enrollment/Incomplete_Template.xlsx",
};

// ─── Presigned URL Types ──────────────────────────────────────────────────────
export interface EnrolmentPresignedUrlRequest {
  fileName: string;
  contentType: string;
  context: string;
}

export interface EnrolmentPresignedUrlResponse {
  presignedUrl: string;
  key?: string;
  fileKey?: string;
}

export type IncompleteBulkUploadEnrolledThrough = "Offline" | "Receivables";

const LIST_ENROLLMENTS_ENDPOINT = "/api/v2/enrollments/admin/list-enrollments";
const ENROLMENTS_ENDPOINT = "/api/v1/admin/academy/enrolments";
const ENROLMENTS_V2_ENDPOINT = "/api/v2/enrollments/admin";
const USERS_ADMIN_ENDPOINT = "/api/v1/users/admin";
const ACADEMY_ADMIN_ENDPOINT = "/api/v1/academy/admin";
const BATCHES_ENDPOINT = "/api/v1/batches";

// ─── Nested Sub-types ────────────────────────────────────────────────────────

export interface EnrolmentPlayer {
  _id: string;
  fullName: string;
  profilePic: string | null;
}

export interface EnrolmentUser {
  _id: string;
  countryCode: number;
  phoneNumber: number;
}

export interface EnrolmentAcademy {
  _id: string;
  basicDetails: {
    academyName: string;
  };
}

export interface EnrolmentCoachingCenter {
  _id: string;
  basicDetails: {
    address: string;
    city: string;
    location: string;
  };
}

export interface EnrolmentFixedSlot {
  _id: string;
  days: string[];
  endTime: string;
  startTime: string;
}

export interface EnrolmentTotalFeeInfo {
  balanceFee?: number;
  coinsDiscount?: number;
  discount: number;
  gstAmt: number;
  promoDiscount?: number;
  referralDiscount?: number;
  regFee: number;
  totalFee: number;
  totalFeeAfterGST: number;
  totalFeeBeforeDiscount: number;
  totalFeeBeforeReg: number;
  totalFeePaid?: number;
}

// ─── Main EnrolmentItem ───────────────────────────────────────────────────────

export interface EnrolmentItem {
  _id: string;
  academy: EnrolmentAcademy;
  ageCategory: string;
  batch: string;
  batchName: string;
  coachAlloted: string | null;
  coachingCenter: EnrolmentCoachingCenter;
  coupon: string | null;
  couponCode: string | null;
  course: string;
  courseName: string;
  createdAt: string;
  endDate: string;
  enrolledBy: string;
  enrolledThrough: string;
  firmName: string | null;
  fixedSlots: EnrolmentFixedSlot[];
  foreclosure: string | null;
  gstNumber: string | null;
  installmentDue: string | null;
  installmentStatus: string;
  installments: unknown[];
  isActive: boolean;
  isCouponApplied: boolean;
  isInstallmentPlan: boolean;
  isOnline: boolean;
  isPaymentCoveredByPoints: boolean;
  isPointRedeemed: boolean;
  isReactive: boolean;
  isReferralDiscountApplied: boolean;
  isRenewed: boolean;
  location: string;
  markedInactiveAt: string | null;
  markedReactiveAt: string | null;
  meetingCreds: string | null;
  note: string | null;
  numberOfInstallments: number;
  paidAt: string;
  parent: string | null;
  paymentFrequency: string;
  paymentType: string;
  player: EnrolmentPlayer;
  pointsRedeemed: number;
  previousEnrollment: string | null;
  previousEnrollmentEndDate: string | null;
  promoDiscountAmount: number;
  promoId: string | null;
  referralDiscountAmount: number;
  referralId: string | null;
  slotType: string;
  slots: unknown[];
  sports: string;
  startDate: string;
  status: string;
  totalFeeInfo: EnrolmentTotalFeeInfo;
  totalSessions: number;
  trainingFrequency: string;
  updatedAt: string;
  user: EnrolmentUser;
}

// ─── List Enrollments ─────────────────────────────────────────────────────────

export interface ListEnrolmentsParams {
  search?: string;
  enrolledThrough?: string;
  sports?: string;
  startDate?: string;
  endDate?: string;
  paidAt?: string;
  pageNum?: number;
  pageSize?: number;
}

export interface ListEnrolmentsResponse {
  data: {
    paginatedResults: EnrolmentItem[];
    totalCount: number;
  };
}

/**
 * List enrollments with search, filters and pagination.
 * Uses GET /api/v2/enrollments/admin/list-enrollments
 */
export const listEnrolments = async (
  params: ListEnrolmentsParams
): Promise<ListEnrolmentsResponse> => {
  const { search, enrolledThrough, sports, startDate, endDate, paidAt, pageNum = 1, pageSize = 20 } =
    params;

  const queryParams: Record<string, string | number> = { pageNum, pageSize };

  if (search?.trim()) queryParams.search = search.trim();
  if (enrolledThrough?.trim()) queryParams.enrolledThrough = enrolledThrough.trim();
  if (sports?.trim()) queryParams.sports = sports.trim();
  if (startDate?.trim()) queryParams.startDate = startDate.trim();
  if (endDate?.trim()) queryParams.endDate = endDate.trim();
  if (paidAt?.trim()) queryParams.paidAt = paidAt.trim();

  const queryString =
    "?" +
    Object.entries(queryParams)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");

  const response = await api.get(`${LIST_ENROLLMENTS_ENDPOINT}${queryString}`, undefined);

  const resp = response as {
    data?: { paginatedResults?: EnrolmentItem[]; totalCount?: number };
  };

  return {
    data: {
      paginatedResults: Array.isArray(resp?.data?.paginatedResults)
        ? resp.data!.paginatedResults
        : [],
      totalCount: resp?.data?.totalCount ?? 0,
    },
  };
};

// ─── Create / Update Payloads ─────────────────────────────────────────────────

export interface CreateEnrolmentPayload {
  playerName: string;
  phoneNumber: string;
  centre?: string;
  sport?: string;
  startDate: string;
  endDate?: string;
  amount?: number;
  enrolledThrough?: string;
}

export interface UpdateEnrolmentPayload {
  playerName?: string;
  phoneNumber?: string;
  centre?: string;
  sport?: string;
  startDate?: string;
  endDate?: string;
  amount?: number;
}

export interface BulkUploadResult {
  successCount: number;
  failedCount: number;
  failedRecords: { row: number; reason: string; data?: Record<string, unknown> }[];
}

// ─── V2 Bulk Upload Result (presigned-URL flow) ───────────────────────────────
export type EnrolmentUploadRecordStatus = "Success" | "Skipped" | "Failed";

export interface EnrolmentUploadRecord {
  player: string;
  status: EnrolmentUploadRecordStatus;
  reason?: string;
}

export interface EnrolmentBulkUploadResult {
  records: EnrolmentUploadRecord[];
  successCount: number;
  skippedCount: number;
  failedCount: number;
  message: string;
  // Validation error mode — when the API returns errors[] instead of a records array
  isValidationError: boolean;
  validationErrors: string[];
  validationMessage: string;
}

export interface BulkUploadResponse {
  data: BulkUploadResult;
}

/**
 * Create a new enrolment (defaults to offline).
 */
export const createEnrolment = async (
  payload: CreateEnrolmentPayload
): Promise<EnrolmentItem> => {
  const body: CreateEnrolmentPayload = {
    enrolledThrough: "offline",
    ...payload,
  };

  const response = await api.create(ENROLMENTS_ENDPOINT, body);

  if (response && typeof response === "object" && "data" in response) {
    return (response as { data: EnrolmentItem }).data;
  }

  throw new Error("Invalid response from create enrolment API");
};

/**
 * Update an existing enrolment.
 * Payment fields (amount) are locked for online enrolments on the backend.
 */
export const updateEnrolment = async (
  id: string,
  payload: UpdateEnrolmentPayload
): Promise<EnrolmentItem> => {
  const response = await api.put(`${ENROLMENTS_ENDPOINT}/${id}`, payload);

  if (response && typeof response === "object" && "data" in response) {
    return (response as { data: EnrolmentItem }).data;
  }

  throw new Error("Invalid response from update enrolment API");
};

/**
 * Bulk upload enrolments via an Excel/CSV file.
 * Returns success count, failed count and per-row error details.
 */
export const bulkUploadEnrolments = async (file: File): Promise<BulkUploadResult> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.create(`${ENROLMENTS_ENDPOINT}/bulk-upload`, formData);

  if (response && typeof response === "object" && "data" in response) {
    return (response as BulkUploadResponse).data;
  }

  throw new Error("Invalid response from bulk upload API");
};

// ─── Download Helpers ─────────────────────────────────────────────────────────

export interface DownloadEnrolmentsParams {
  search?: string;
  enrolledThrough?: string;
  sports?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Download filtered enrollment data as a file (blob).
 */
export const downloadEnrolments = async (
  params: DownloadEnrolmentsParams
): Promise<Blob> => {
  const queryParams: Record<string, string> = {};

  if (params.search?.trim()) queryParams.search = params.search.trim();
  if (params.enrolledThrough?.trim()) queryParams.enrolledThrough = params.enrolledThrough.trim();
  if (params.sports?.trim()) queryParams.sports = params.sports.trim();
  if (params.startDate?.trim()) queryParams.startDate = params.startDate.trim();
  if (params.endDate?.trim()) queryParams.endDate = params.endDate.trim();

  const queryString =
    Object.keys(queryParams).length > 0
      ? "?" +
        Object.entries(queryParams)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join("&")
      : "";

  const response = await api.get(
    `${ENROLMENTS_ENDPOINT}/download${queryString}`,
    undefined
  );

  if (response instanceof Blob) return response;

  return new Blob([JSON.stringify(response)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

/**
 * Download the bulk-upload Excel template.
 */
export const downloadEnrolmentsTemplate = async (): Promise<Blob> => {
  const response = await api.get(`${ENROLMENTS_ENDPOINT}/template`, undefined);

  if (response instanceof Blob) return response;

  return new Blob([JSON.stringify(response)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// ─── Presigned URL + V2 Bulk Upload ──────────────────────────────────────────

/**
 * Get a presigned S3 URL for direct file upload.
 * context: "spreadsheets" for Excel files.
 */
export const getEnrolmentPresignedUrl = async (
  data: EnrolmentPresignedUrlRequest
): Promise<EnrolmentPresignedUrlResponse> => {
  const response = await secureApi.post<any>("/api/v1/general/presigned-url", data);

  if (response && response.data && (response.data.presignedUrl || response.data.key)) {
    return {
      presignedUrl: response.data.presignedUrl,
      key: response.data.key,
      fileKey: response.data.key || response.data.fileKey,
    };
  } else if (response && (response.presignedUrl || response.key)) {
    return {
      presignedUrl: response.presignedUrl,
      key: response.key,
      fileKey: response.key || response.fileKey,
    };
  }

  return response;
};

// Processing large files can take longer than the default 10s axios timeout
const BULK_UPLOAD_TIMEOUT = 60_000; // 1 minute

/**
 * Bulk upload Dynamic enrolments using a pre-uploaded file key.
 */
export const bulkUploadDynamic = async (fileKey: string): Promise<any> => {
  const response = await secureApi.post<any>(
    "/api/v2/enrollments/admin/bulk-upload/dynamic",
    { fileKey },
    { timeout: BULK_UPLOAD_TIMEOUT }
  );
  return response;
};

/**
 * Bulk upload Fixed enrolments using a pre-uploaded file key.
 */
export const bulkUploadFixed = async (fileKey: string): Promise<any> => {
  const response = await secureApi.post<any>(
    "/api/v2/enrollments/admin/bulk-upload/fixed",
    { fileKey },
    { timeout: BULK_UPLOAD_TIMEOUT }
  );
  return response;
};

/**
 * Bulk upload Incomplete enrolments using a pre-uploaded file key.
 */
export const bulkUploadIncomplete = async (
  fileKey: string,
  enrolledThrough: IncompleteBulkUploadEnrolledThrough = "Offline"
): Promise<any> => {
  const response = await secureApi.post<any>(
    "/api/v2/enrollments/admin/bulk-upload/incomplete",
    { fileKey, enrolledThrough },
    { timeout: BULK_UPLOAD_TIMEOUT }
  );
  return response;
};

// ─── Search / Add Profiles ────────────────────────────────────────────────────

export interface PlayerProfile {
  _id: string;
  dob: string | null;
  email: string | null;
  fullName: string;
  gender: string | null;
  profilePic: string | null;
  relationshipType: "self" | "parent" | string;
}

export interface SearchProfilesData {
  email: string | null;
  fullName: string;
  location: string;
  phoneNumber: number;
  players: PlayerProfile[];
  user: string;
}

export interface SearchProfilesResponse {
  data: SearchProfilesData;
}

/** Flat error shape returned by the search-profiles API on 404. */
export interface SearchProfilesErrorBody {
  error: string;
  status: string | null;
  statusCode: number;
  title: string | null;
}

/** Thrown when search-profiles returns 404. Carries the API error message. */
export class UserNotFoundError extends Error {
  constructor(public readonly apiMessage: string) {
    super(apiMessage);
    this.name = "UserNotFoundError";
  }
}

export const searchProfilesByPhone = async (
  phoneNumber: number
): Promise<SearchProfilesData> => {
  try {
    const response = await api.create(`${USERS_ADMIN_ENDPOINT}/search-profiles`, { phoneNumber });
    const resp = response as { data?: SearchProfilesData };
    if (resp?.data) return resp.data;
    // Success response without data — treat as "not found"
    throw new UserNotFoundError("User not found");
  } catch (err: unknown) {
    if (err instanceof UserNotFoundError) throw err;

    const anyErr = err as any;
    // The API returns a flat object: { error, status, statusCode, title }
    // axios exposes that body on err.response.data
    const body: Partial<SearchProfilesErrorBody> | undefined = anyErr?.response?.data;
    const httpStatus: number | undefined = anyErr?.response?.status;

    const is404 = httpStatus === 404 || body?.statusCode === 404;
    if (is404) {
      throw new UserNotFoundError(body?.error || "User not found");
    }
    throw err;
  }
};

export interface AddProfileParent {
  fullName: string;
  gender: string;
  dob: string;
}

export interface AddProfilePlayer {
  fullName: string;
  gender: string;
  dob: string;
}

export interface AddProfilePayload {
  phoneNumber?: number;
  parent?: AddProfileParent;
  player?: AddProfilePlayer;
}

export interface CreatedProfile {
  _id: string;
  fullName: string;
  gender: string | null;
  dob: string | null;
  profilePic: string | null;
  relationshipType: "self" | "parent" | string;
  user: string;
}

export interface AddProfileResponse {
  // Present when isExistingUser === false (new profiles were created)
  createdProfiles?: CreatedProfile[];
  // Present when isExistingUser === true (user already existed, full player list returned)
  players?: CreatedProfile[];
  addedProfiles?: CreatedProfile[];
  isExistingUser: boolean;
  phoneNumber: number;
  user: string;
  fullName?: string;
  email?: string | null;
  location?: string | null;
}

export const addProfile = async (
  payload: AddProfilePayload
): Promise<AddProfileResponse> => {
  const response = await api.create(`${USERS_ADMIN_ENDPOINT}/add-profile`, payload);
  const resp = response as { data?: AddProfileResponse };
  if (resp?.data) return resp.data;
  throw new Error("Invalid response from add-profile API");
};

// ─── Admin Academy / Center / Batch Lists ────────────────────────────────────

export interface AcademyListItem {
  _id: string;
  basicDetails: {
    academyName: string;
    sports: string[];
  };
}

export const listAdminAcademies = async (): Promise<AcademyListItem[]> => {
  const response = await api.get(`${ACADEMY_ADMIN_ENDPOINT}/list`, undefined);
  const resp = response as { data?: AcademyListItem[] };
  return Array.isArray(resp?.data) ? resp.data! : [];
};

export interface CenterListItem {
  _id: string;
  basicDetails: {
    address: string;
    location: string;
  };
}

export const listAdminCenters = async (academyId: string): Promise<CenterListItem[]> => {
  const response = await api.get(`${ACADEMY_ADMIN_ENDPOINT}/list-centers?academy=${academyId}`, undefined);
  const resp = response as { data?: CenterListItem[] };
  return Array.isArray(resp?.data) ? resp.data! : [];
};

export const listAgeCategoriesByCenter = async (centerId: string): Promise<string[]> => {
  const response = await api.get(`${BATCHES_ENDPOINT}/fetch-age-category-by-center/${centerId}`, undefined);
  const resp = response as { data?: { ageCategories?: string[] } };
  return Array.isArray(resp?.data?.ageCategories) ? resp.data!.ageCategories! : [];
};

export interface BatchFeeInfo {
  [feeKey: string]: number | null;
  halfYearlyFee: number | null;
  monthlyFee: number | null;
  quarterlyFee: number | null;
  yearlyFee: number | null;
}

export interface BatchFee {
  _id: string;
  feeInfo: BatchFeeInfo;
  trainingFrequency: string;
}

export interface BatchFixedSlot {
  _id: string;
  canEnroll: boolean;
  days: string[];
  endTime: string;
  isFreeTrialAvailable: boolean;
  startTime: string;
}

export interface BatchDynamicSlot {
  _id?: string;
  day: string;
  startTime: string;
  endTime: string;
}

export interface BatchListItem {
  _id: string;
  ageCategory: string;
  batchName: string;
  course: string;
  fees: BatchFee[];
  fixedSlots: BatchFixedSlot[];
  slotType: "fixed" | "dynamic" | string;
  slots: BatchDynamicSlot[];
  trainingDays: string[];
}

export interface ListBatchesResponse {
  paginatedResults: BatchListItem[];
  totalCount: number;
}

export const listAdminBatches = async (centerId: string): Promise<BatchListItem[]> => {
  const response = await api.get(`${BATCHES_ENDPOINT}/admin/list/${centerId}?pageNum=1&pageSize=200`, undefined);
  const resp = response as { data?: { paginatedResults?: BatchListItem[] } };
  return Array.isArray(resp?.data?.paginatedResults) ? resp.data!.paginatedResults! : [];
};

export interface BatchDetails extends BatchListItem {
  academy: string;
  batchType: string;
  coachingCenter: string;
  startDate: string | null;
  endDate: string | null;
  sports: string;
  isRecurring: boolean;
  isCancelled: boolean;
}

export const getBatchById = async (batchId: string): Promise<BatchDetails> => {
  const response = await api.get(`${BATCHES_ENDPOINT}/find-by-id/${batchId}`, undefined);
  const resp = response as { data?: BatchDetails };
  if (resp?.data) return resp.data;
  throw new Error("Invalid response from batch find-by-id API");
};

export interface PlanField {
  feeKey: string;
  frequency: string;
  label: string;
  order: number;
}

export const fetchPlanFields = async (): Promise<PlanField[]> => {
  const response = await api.get(`${BATCHES_ENDPOINT}/plan-fields`, undefined);
  const resp = response as { data?: PlanField[] };
  return Array.isArray(resp?.data) ? resp.data : [];
};

// ─── Enrolment Details ────────────────────────────────────────────────────────

export interface EnrolmentDetails {
  _id: string;
  academy: EnrolmentAcademy;
  ageCategory: string;
  batch: string;
  batchName: string;
  coachingCenter: EnrolmentCoachingCenter;
  course: string;
  courseName: string;
  createdAt: string;
  endDate: string;
  enrolledBy: string;
  enrolledThrough: string;
  fixedSlots: EnrolmentFixedSlot[];
  isActive: boolean;
  location: string;
  paidAt?: string;
  parent: string | null;
  paymentAmount?: number;
  paymentFrequency: string;
  paymentType: string;
  player: EnrolmentPlayer;
  slotType: string;
  slots: BatchDynamicSlot[];
  sports: string;
  startDate: string;
  status: string;
  totalFeeInfo: EnrolmentTotalFeeInfo;
  totalSessions: number;
  trainingFrequency: string;
  updatedAt: string;
  user: EnrolmentUser;
}

export const getEnrolmentDetails = async (id: string): Promise<EnrolmentDetails> => {
  const response = await api.get(`${ENROLMENTS_V2_ENDPOINT}/details/${id}`, undefined);
  const resp = response as { data?: EnrolmentDetails };
  if (resp?.data) return resp.data;
  throw new Error("Invalid response from enrolment details API");
};

// ─── V2 Add / Update Enrolment ────────────────────────────────────────────────

export interface CreateEnrolmentV2Payload {
  enrolledThrough: "Excel" | "Offline";
  enrolledBy: "self" | "parent";
  user: string;
  player: string;
  academy: string;
  coachingCenter: string;
  batch: string;
  batchName: string;
  course: string;
  ageCategory: string;
  slotType: "fixed" | "dynamic";
  sports: string;
  location: string;
  startDate: string;
  trainingFrequency: string;
  paymentFrequency: string;
  paymentAmount: number;
  fixedSlots?: { days: string[]; startTime: string; endTime: string }[];
  slots?: { day: string; startTime: string; endTime: string }[];
}

export interface UpdateEnrolmentV2Payload {
  academy?: string;
  coachingCenter?: string;
  batch?: string;
  ageCategory?: string;
  slotType?: "fixed" | "dynamic";
  trainingFrequency: string;
  paymentFrequency: string;
  startDate: string;
  paymentAmount?: number;
  fixedSlots?: { days: string[]; startTime: string; endTime: string }[];
  slots?: { day: string; startTime: string; endTime: string }[];
}

export const addEnrolmentV2 = async (
  payload: CreateEnrolmentV2Payload
): Promise<unknown> => {
  const response = await api.create(`${ENROLMENTS_V2_ENDPOINT}/add-enrollment`, payload);
  return response;
};

// ─── Offline Enrolment ────────────────────────────────────────────────────────

export interface CreateOfflineEnrolmentPayload {
  enrolledThrough: "Offline";
  user: string | null;
  player: string | null;
  academy: string;
  coachingCenter: string;
  sports: string;
  location: string;
  startDate: string;
  endDate: string;
  paidAt: string;
  paymentAmount: number;
  slotType?: "fixed" | "dynamic";
  batch?: string;
  batchName?: string;
  course?: string;
  ageCategory?: string;
  trainingFrequency?: string;
  paymentFrequency?: string;
  fixedSlots?: { days: string[]; startTime: string; endTime: string }[];
  slots?: { day: string; startTime: string; endTime: string }[];
}

export const addOfflineEnrolment = async (
  payload: CreateOfflineEnrolmentPayload
): Promise<unknown> => {
  const response = await api.create(`${ENROLMENTS_V2_ENDPOINT}/add-enrollment`, payload);
  return response;
};

export const updateEnrolmentV2 = async (
  id: string,
  payload: UpdateEnrolmentV2Payload
): Promise<unknown> => {
  const response = await api.create(`${ENROLMENTS_V2_ENDPOINT}/update-enrollment/${id}`, payload);
  return response;
};

export interface UpdateOfflineEnrolmentPayload {
  user?: string | null;
  player?: string | null;
  academy?: string;
  coachingCenter?: string;
  sports?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  paidAt?: string;
  paymentAmount?: number;
  slotType?: "fixed" | "dynamic";
  batch?: string;
  batchName?: string;
  course?: string;
  ageCategory?: string;
  trainingFrequency?: string;
  paymentFrequency?: string;
  fixedSlots?: { days: string[]; startTime: string; endTime: string }[];
  slots?: { day: string; startTime: string; endTime: string }[];
}

export const updateOfflineEnrolment = async (
  id: string,
  payload: UpdateOfflineEnrolmentPayload
): Promise<unknown> => {
  const response = await api.create(`${ENROLMENTS_V2_ENDPOINT}/update-enrollment/${id}`, payload);
  return response;
};

/**
 * Delete one or more enrolments by ID.
 * POST /api/v2/enrollments/admin/delete-enrollment
 */
export const deleteEnrollments = async (enrollmentIds: string[]): Promise<unknown> => {
  return secureApi.post(`${ENROLMENTS_V2_ENDPOINT}/delete-enrollment`, { enrollmentIds });
};

/**
 * Toggle the active/inactive status of an enrolment.
 * POST /api/v2/enrollments/mark/inactive
 */
export const toggleEnrolmentActiveStatus = async (
  enrollmentId: string
): Promise<EnrolmentItem> => {
  const response = await secureApi.post<{ data: EnrolmentItem }>(
    "/api/v2/enrollments/mark/inactive",
    { enrollmentId }
  );
  const data = (response as unknown as { data?: EnrolmentItem })?.data;
  if (data) return data;
  throw new Error("Invalid response from mark inactive API");
};

// ─── Website User Creation ────────────────────────────────────────────────────

export interface CreateWebsiteUserPayload {
  countryCode: number;
  phoneNumber: number;
}

export interface CreateWebsiteUserData {
  _id: string;
  countryCode: number;
  phoneNumber: number;
}

export const createWebsiteUser = async (
  payload: CreateWebsiteUserPayload
): Promise<CreateWebsiteUserData> => {
  const response = await api.create("api/v1/auth/website/create-user", payload);
  const resp = response as { data?: CreateWebsiteUserData };
  if (resp?.data) return resp.data;
  throw new Error("Invalid response from create-user API");
};
